import { useQuery } from '@tanstack/react-query'

import { StaleTime } from '@core/query'
import { convertComment } from '@core/converters'
import { CommentQueryKey } from '../helpers/queryKeys'

import { getAudioComments } from '../services/getAudioComments'
import { getProjectComments } from '../services/getProjectComments'
import { type TCommentsEntityType } from '../types/commentsEntityTypes'

type TUseCommentsOptions = {
    entityType?: TCommentsEntityType
    enabled?: boolean
}

/**
 * Read the comment list (with embedded replies) for an audio track or a project. Public read — no
 * session required, so this is fully exercisable before the auth cutover. DTOs are mapped to domain
 * models here; the list is keyed by `CommentQueryKey.comments(entityId)` so the mutation hooks can splice it.
 */
export const useComments = (entityId: string, options?: TUseCommentsOptions) => {
    const entityType = options?.entityType ?? 'audio'

    return useQuery({
        queryKey: CommentQueryKey.comments(entityId),
        queryFn: async ({ signal }) => {
            const apiComments =
                entityType === 'audio'
                    ? await getAudioComments(entityId, signal)
                    : await getProjectComments(entityId, signal)

            return apiComments.map(convertComment)
        },
        enabled: options?.enabled,
        staleTime: StaleTime.Short,
    })
}
